import { ContractStatus } from '../../types/contracts';
import { Check, XCircle } from 'lucide-react';

interface ContractTimelineProps {
  status: ContractStatus;
  onStageClick?: (status: ContractStatus) => void;
}

interface TimelineStep {
  key: ContractStatus | 'negotiating';
  label: string;
  maps: ContractStatus[];
}

const STEPS: TimelineStep[] = [
  { key: 'draft',       label: 'Draft',       maps: ['draft'] },
  { key: 'sent',        label: 'Sent',         maps: ['sent'] },
  { key: 'negotiating', label: 'Negotiating',  maps: ['viewed', 'countered', 'accepted'] },
  { key: 'sent',        label: 'Signed',       maps: [] }, // placeholder — completed by signatures
  { key: 'draft',       label: 'Executed',     maps: [] }, // placeholder
];

// Map contract status → step index (0-based)
function getStepIndex(status: ContractStatus): number {
  switch (status) {
    case 'draft':     return 0;
    case 'sent':      return 1;
    case 'viewed':    return 2;
    case 'countered': return 2;
    case 'accepted':  return 3;
    case 'executed':  return 4;
    case 'rejected':  return -1; // special case
    default:          return 0;
  }
}

const TIMELINE_STEPS = [
  { label: 'Draft',      statuses: ['draft'] as ContractStatus[] },
  { label: 'Sent',       statuses: ['sent'] as ContractStatus[] },
  { label: 'Negotiating',statuses: ['viewed', 'countered'] as ContractStatus[] },
  { label: 'Accepted',   statuses: ['accepted'] as ContractStatus[] },
  { label: 'Executed',   statuses: ['executed'] as ContractStatus[] },
];

export function ContractTimeline({ status, onStageClick, className = "px-8 py-4 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0" }: ContractTimelineProps & { className?: string }) {
  const isRejected = status === 'rejected';

  // Find which step index the current status falls in
  const currentStepIdx = TIMELINE_STEPS.findIndex(step =>
    (step.statuses as ContractStatus[]).includes(status)
  );

  // For rejected: figure out which step it was rejected at
  // We treat rejected as the step it was at when rejected (conservatively, step 2 = Negotiating)
  const rejectedAtIdx = isRejected ? 2 : -1;

  return (
    <div className={className}>
      <div className="flex items-center gap-0 w-full max-w-3xl mx-auto">
        {TIMELINE_STEPS.map((step, idx) => {
          const isCompleted = !isRejected && currentStepIdx > idx;
          const isCurrent = !isRejected && currentStepIdx === idx;
          const isRejectedHere = isRejected && rejectedAtIdx === idx;
          const isFuture = isRejected
            ? idx > rejectedAtIdx
            : currentStepIdx < idx;
          const isLastStep = idx === TIMELINE_STEPS.length - 1;

          const isPrevious = !isRejected && currentStepIdx > idx;

          return (
            <div key={step.label} className="flex items-center flex-1 min-w-0">
              {/* Step node */}
              <div 
                className={`flex flex-col items-center shrink-0 ${isPrevious ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
                onClick={() => {
                  if (isPrevious && onStageClick) {
                    const targetStatus = step.statuses.includes('countered') ? 'countered' : step.statuses[0];
                    onStageClick(targetStatus);
                  }
                }}
              >
                <div className={`
                  w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black border transition-all
                  ${isCompleted
                    ? 'bg-[var(--t-primary)] border-[var(--t-primary)] text-white hover:bg-[var(--t-primary-hover)]'
                    : isCurrent
                    ? 'bg-[var(--t-primary-dim)] border-[var(--t-primary)] text-[var(--t-primary)] ring-2 ring-[var(--t-primary)]/20'
                    : isRejectedHere
                    ? 'bg-[var(--t-error)]/10 border-[var(--t-error)] text-[var(--t-error)]'
                    : isFuture
                    ? 'bg-[var(--t-surface-hover)] border-[var(--t-border)] text-[var(--t-text-muted)]'
                    : 'bg-[var(--t-surface-hover)] border-[var(--t-border)] text-[var(--t-text-muted)]'
                  }
                `}>
                  {isCompleted
                    ? <Check size={10} strokeWidth={3} />
                    : isRejectedHere
                    ? <XCircle size={10} />
                    : <span>{idx + 1}</span>
                  }
                </div>
                <span className={`
                  text-[9px] font-semibold mt-0.5 whitespace-nowrap
                  ${isCompleted ? 'text-[var(--t-primary)]'
                    : isCurrent ? 'text-[var(--t-primary)] font-black'
                    : isRejectedHere ? 'text-[var(--t-error)]'
                    : 'text-[var(--t-text-muted)]'
                  }
                `}>
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLastStep && (
                <div className="flex-1 h-0.5 mx-2 rounded-full transition-all" style={{
                  background: isCompleted && !isRejected
                    ? 'var(--t-primary)'
                    : 'var(--t-border)',
                  opacity: isFuture ? 0.4 : 1
                }} />
              )}
            </div>
          );
        })}

        {/* Rejected tag at end if rejected */}
        {isRejected && (
          <div className="flex items-center ml-2 shrink-0">
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--t-error)]/10 text-[var(--t-error)] text-[10px] font-black uppercase tracking-wide">
              <XCircle size={10} /> Rejected
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
